import { Alert, Box, Grid } from '@mui/material'
import { Form, Formik } from 'formik'
import { DateTime } from 'luxon'
import React from 'react'
import { PatientHistoryType } from 'src/graphql.generated'
import useAddPatientHistoryEntry from 'src/participantHistory/useAddPatientHistoryEntry'
import { toFormikValidationSchema } from 'zod-formik-adapter'
import { Loader } from 'src/designSystem'
import { NoteDraftLoadError } from 'src/noteDraft/NoteDraftAlerts'
import AdvancedFollowUpNoteFields from './AdvancedFollowUpNoteFields'
import { clearFormFromLocalStorage } from '../persistFormHelpers'
import FormPersistor from '../FormPersistor'
import { ADVANCED_FOLLOW_UP_NOTE_FORM_SCHEMA, AdvancedFollowUpNoteFormType } from './AdvancedFollowUpNoteFormSchema'
import { useLocalStorageWithPrefillValues } from '../useLocalStorageWithPrefillValues'
import ConfirmNoteDialog from '../ConfirmNoteDialog'

const getDefaultValues = () => ({
  date: DateTime.now().toFormat('yyyy-MM-dd'),
  FIRST_NAME: '',
  LAST_NAME: '',
  MEMBER_ID: '',
  PROVIDER_NAME: '',
  LICENSE_TYPE: '',
  SUBJECTIVE: {
    CHIEF_CONCERN: '',
    UPDATES_SINCE_LAST_VISIT: '',
    MENTAL_HEALTH_STATUS: '',
    PHYSICAL_SYMPTOMS: '',
    MEDICATION_SUPPLEMENT_UPDATES: '',
    SAFETY_CONCERNS: '',
  },
  OBJECTIVE: {
    RELEVANT_METRICS_OR_LABS: '',
    BEHAVIORAL_OBSERVATIONS_OR_OTHER_PERTINENT_FINDINGS: '',
  },
  ASSESSMENT: {
    CLINICAL_SUMMARY: '',
  },
  PLAN: {
    FOCUS_AREAS: '',
    LIFESTYLE_GOALS: '',
    NUTRITION_METABOLIC_ADJUSTMENTS: '',
    MENTAL_HEALTH_SOMATIC_TOOLS: '',
    MEDICATIONS_SUPPLEMENTS: '',
    REFERRALS_ESCALATION: '',
    NEXT_SCHEDULED_SESSION: '',
    PATIENT_EXPRESSED_UNDERSTANDING: false,
    PATIENT_AGREES_WITH_PLAN: false,
  },
})

type Props = {
  formType: PatientHistoryType.AdvancedFollowUpNote
  uid: string
  afterSubmittedCallback?: () => void
}

const AdvancedFollowUpNoteForm = ({ formType, uid, afterSubmittedCallback }: Props) => {
  const addPatientHistoryEntry = useAddPatientHistoryEntry()

  const { values: initialValues, status } = useLocalStorageWithPrefillValues(uid, formType, getDefaultValues)

  if (status === 'draftError') {
    return <NoteDraftLoadError />
  }
  if (status === 'loading') {
    return <Loader size="large" />
  }

  return (
    <Grid item>
      <Formik<AdvancedFollowUpNoteFormType>
        enableReinitialize
        initialValues={initialValues}
        validationSchema={toFormikValidationSchema(ADVANCED_FOLLOW_UP_NOTE_FORM_SCHEMA)}
        onSubmit={async (values, { resetForm }) => {
          await addPatientHistoryEntry({
            uid,
            date: values.date,
            type: formType,
            value: values,
          })
          resetForm({ values: getDefaultValues() })
          clearFormFromLocalStorage(uid, formType)
          afterSubmittedCallback?.()
        }}
      >
        {({ isSubmitting, submitForm }) => (
          <Form>
            <FormPersistor uid={uid} formType={formType} />
            {status === 'error' && (
              <Box mt={3}>
                <Alert severity="error">Failed to load form prefill data.</Alert>
              </Box>
            )}

            <AdvancedFollowUpNoteFields />
            <Box>
              <ConfirmNoteDialog
                isSubmitting={isSubmitting}
                onSubmit={submitForm}
                buttonLabel="Follow Up Note is complete"
                dialogTitle="Complete Follow Up Note"
              />
            </Box>
          </Form>
        )}
      </Formik>
    </Grid>
  )
}

export default AdvancedFollowUpNoteForm
